import type {
  Experience,
  Order,
  BidPurchaseOption,
  PurchaseMode,
  PaymentOption,
} from '../../../../../types/CoreShoppingExperience';
import convertToRefundEvent from '../utils/convertToRefundEvent';
import convertToPurchaseEvent from '../utils/convertToPurchaseEvent';
import convertToModifyEvent from '../utils/convertToModifyEvent';
import convertToViewItemListEvent from '../utils/convertToViewItemListEvent';
import convertToViewCartEvent from '../utils/convertToViewCartEvent';
import {
  type GoogleAnalyticPage,
  GoogleAnalyticsEventType,
  GoogleAnalyticsSeatMapClick,
  type GoogleAnalyticsCartClickLocation,
  type GoogleAnalyticsPerformance,
} from '../../../../../types/analytics/GoogleAnalyticsEvent';
import usePublishGoogleAnalyticsEventMutation from './usePublishGoogleAnalyticsEventMutation';
import useAppConfig from 'canvas_states/config/useAppConfig';
import useExperience from 'canvas_states/experience/useExperience';
import useShoppingCart from 'canvas_states/shoppingCart/useShoppingCart';
import useUrlInfo from '../../urls/useUrlInfo';
import type { Configuration } from '../../../../../types/Configuration';
import useRealTimeBidding from 'canvas_states/realTimeBidding/useRealTimeBidding';
import type { RealTimeBiddingData } from '../../../../../types/analytics/GoogleAnalytics';
import { hasContinuousPricingFromExperience } from '../utils/hasContinuousPricing';
import useConvertToAddRemoveCartEvent from '../utils/useConvertToAddRemoveCartEvent';
import type { SeatMapInteractionEventParams } from '../useDynamicSeatBlockerEvents';
import type { OptionalEventParams, OptionalItems } from '../../../../../types/analytics/AnalyticsEvent';
import convertFallbackToViewItemListEvent from '../utils/convertFallbackToViewItemListEvent';
import type { OptimizelyDecision } from '@optimizely/react-sdk';

/**
 * This hook should ONLY be used in analytics folder
 * We want to hide the events implementation (GA + Quantify) under the useXXXEvents.ts
 */
const useGoogleAnalytics = () => {
  const publisher = usePublishGoogleAnalyticsEventMutation();
  const experience = useExperience().states.experience;
  const { requestId } = useUrlInfo().states;
  const { getRealTimeBiddingByOrderId, isShowRealTimeBidding } = useRealTimeBidding().selectors;
  const config: Configuration = useAppConfig().states.config;
  const { shoppingCart } = useShoppingCart().states;

  const publishPageView = async (page: GoogleAnalyticPage) => {
    publisher.actions.publish(GoogleAnalyticsEventType.PAGE_VIEW, {
      page_params: {
        location: window.location.href,
        title: page,
      },
    });
  };

  const publishConfig = async () => {
    publisher.actions.publish(GoogleAnalyticsEventType.CONFIG, {
      partner_name: config?.companyCode,
    });
  };

  const publishPartialPageView = async (page: GoogleAnalyticPage) => {
    publisher.actions.publish(GoogleAnalyticsEventType.PAGE_VIEW_PARTIAL, {
      page_params: {
        location: window.location.href,
        title: page,
      },
    });
  };
  const publishDefaultLayer = async (experience: Experience | undefined) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.DEFAULT, {
      currency_code: experience.currencyCode,
      language: experience.language,
      channel_source: experience.channelSource,
      partner_name: config?.companyCode,
      partner_type: config?.labsPartnerType ?? config?.partnerType,
      continuous_pricing: hasContinuousPricingFromExperience(experience),
      journey: {
        is_multi_city: experience.journey.multiCity,
        trip_type: experience.journey.tripType,
        departure_date: experience.journey.departureDate,
        return_date: experience.journey.returnDate,
        origin_name: experience.journey.originName,
        destination_name: experience.journey.destinationName,
      },
      booking: {
        requestId,
        pax: experience.booking.pax,
      },
    });
  };

  const publishDefaultLayerWithoutExperience = async () => {
    publisher.actions.publish(GoogleAnalyticsEventType.DEFAULT, {
      partner_name: config?.companyCode,
      partner_type: config?.labsPartnerType ?? config?.partnerType,
    });
  };

  const publishPurchaseEvent = async (experience: Experience | undefined) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.PURCHASE, {
      continuous_pricing: hasContinuousPricingFromExperience(experience),
      ecommerce: convertToPurchaseEvent(requestId, shoppingCart, experience, config),
    });
  };

  const publishModifyBidEvent = (order: Order, purchaseOption: BidPurchaseOption) => {
    if (!experience) {
      return;
    }
    const realTimeBidding = getRealTimeBiddingByOrderId(order.id);
    const hasRealTimeBidding = isShowRealTimeBidding(realTimeBidding);
    const realTimeBiddingData = hasRealTimeBidding
      ? {
          isRTB: hasRealTimeBidding,
          seatsAvailable: realTimeBidding?.numberOfSeatsAvailable,
          activeBidders: realTimeBidding?.numberOfBidders,
          rtbBiddingClose: realTimeBidding?.auctionClosingTimestamp,
        }
      : undefined;
    const ecommerce = convertToModifyEvent(
      requestId,
      order,
      purchaseOption,
      experience,
      config,
      realTimeBiddingData as RealTimeBiddingData,
    );
    if (ecommerce) {
      publisher.actions.publish(GoogleAnalyticsEventType.MODIFY, {
        ecommerce,
      });
    }
  };

  const publishCancelEvent = async (orderId: string) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.REFUND, {
      ecommerce: convertToRefundEvent(requestId, orderId, experience, config),
    });
  };

  const publishTogglePointsEvent = (paymentOption: PaymentOption) => {
    publisher.actions.publish(GoogleAnalyticsEventType.TOGGLE_POINTS, { paymentOption });
  };

  const publishInsufficientPointsBalanceEvent = async () => {
    publisher.actions.publish(GoogleAnalyticsEventType.INSUFFICIENT_POINTS, {});
  };

  const publishViewItemList = async (experience: Experience | undefined) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.VIEW_ITEM_LIST, {
      continuous_pricing: hasContinuousPricingFromExperience(experience),
      ecommerce: { items: convertToViewItemListEvent(experience).items },
    });
  };

  const publishViewIneligibleItemList = async (
    experience: Experience | undefined,
    partialExperience: Experience | undefined,
  ) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.VIEW_INELIGIBLE_ITEM_LIST, {
      ecommerce: { items: convertToViewItemListEvent(experience, partialExperience).ineligibleItems },
    });
  };

  const publishLanguageChange = async (language: string) => {
    publisher.actions.publish(GoogleAnalyticsEventType.CHANGE_LANGUAGE, { language });
  };

  const publishOpenDevPanel = async () => {
    publisher.actions.publish(GoogleAnalyticsEventType.OPEN_DEV_PANEL, {});
  };

  const publishCloseCookieBanner = async () => {
    publisher.actions.publish(GoogleAnalyticsEventType.CLOSE_COOKIE_BANNER, {});
  };

  const publishSliderInteractionEvent = async (value: number, min: number, max: number, currency: string) => {
    publisher.actions.publish(GoogleAnalyticsEventType.SLIDER_INTERACTION, {
      event_params: { value, min, max, currency },
    });
  };

  const publishCartClick = async (location: GoogleAnalyticsCartClickLocation) => {
    publisher.actions.publish(GoogleAnalyticsEventType.CART_CLICK, {
      event_params: {
        click_location: location,
      },
    });
  };

  const publishViewCart = async () => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.VIEW_CART, {
      ecommerce: convertToViewCartEvent(requestId, shoppingCart, experience, config),
    });
  };

  const publishBeginCheckout = async () => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.BEGIN_CHECKOUT, {
      ecommerce: convertToViewCartEvent(requestId, shoppingCart, experience, config),
    });
  };

  const { convertToAddRemoveCartEvent } = useConvertToAddRemoveCartEvent().actions;

  const publishAddToCart = async (
    variantEncryptedId: string,
    quantity: number,
    textkeyGroup: string,
    upgradeType: string,
    bidAmount: number,
    pointsAmount?: number,
    purchaseMode?: PurchaseMode,
    eventParams?: OptionalEventParams,
    optionalEcommerceItems?: OptionalItems,
    // eslint-disable-next-line max-params
  ) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.ADD_TO_CART, {
      ...eventParams,
      ecommerce: convertToAddRemoveCartEvent(
        variantEncryptedId,
        quantity,
        textkeyGroup,
        upgradeType,
        bidAmount,
        requestId,
        pointsAmount,
        purchaseMode,
        optionalEcommerceItems,
      ),
    });
  };

  const publishRemoveFromCart = async (
    variantEncryptedId: string,
    quantity: number,
    textkeyGroup: string,
    upgradeType: string,
    bidAmount: number,
    optionalEcommerceItems?: OptionalItems,
  ) => {
    if (!experience) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.REMOVE_FROM_CART, {
      ecommerce: convertToAddRemoveCartEvent(
        variantEncryptedId,
        quantity,
        textkeyGroup,
        upgradeType,
        bidAmount,
        requestId,
        0,
        undefined,
        optionalEcommerceItems,
      ),
    });
  };

  const publishClick = async (text: string, location?: string) => {
    publisher.actions.publish(GoogleAnalyticsEventType.CLICK, {
      event_params: {
        click_text: text,
        click_location: location,
      },
    });
  };

  const publishPageLoadPerformance = async (googleAnalyticsPerformance: GoogleAnalyticsPerformance) => {
    try {
      const elapsedTime = performance.now();
      if (!sessionStorage.getItem(googleAnalyticsPerformance)) {
        publisher.actions.publish(GoogleAnalyticsEventType.PERFORMANCE, {
          event_params: { performance_name: googleAnalyticsPerformance, value: elapsedTime },
        });
        sessionStorage.setItem(googleAnalyticsPerformance, 'sent');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const publishCheckEligibilitySubmit = async () => {
    publisher.actions.publish(GoogleAnalyticsEventType.CHECK_ELIGIBILITY_SUBMIT, {});
  };

  const publishCheckEligibilityResponse = async (eligibilityStatus?: string, requestId?: string) => {
    publisher.actions.publish(GoogleAnalyticsEventType.CHECK_ELIGIBILITY_RESPONSE, {
      event_params: { eligibilityStatus },
      booking: { requestId },
    });
  };

  const publishSeatMapToggle = async (isOpen: boolean) => {
    publisher.actions.publish(GoogleAnalyticsSeatMapClick.TOGGLE_SEATMAP, { event_params: { seatmap_open: isOpen } });
  };

  const publishSeatMapSeatInteraction = async (seatMapEventParams: SeatMapInteractionEventParams) => {
    publisher.actions.publish(seatMapEventParams.eventType, {
      event_params: {
        seats: seatMapEventParams.paxSeats,
        blocked_seats: seatMapEventParams.blockedSeats,
        error_message: seatMapEventParams?.errorMessage,
        isEntireRow: seatMapEventParams?.isEntireRow,
      },
    });
  };

  const publishOptimizely = (experimentName: string, experimentVariant: string, decision: OptimizelyDecision) => {
    publisher.actions.publish(GoogleAnalyticsEventType.OPTIMIZELY, {
      event: 'optimizely_experiment',
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
      optimizely_decision: decision,
    });
  };

  const publishViewFallbackItemListEvent = async (experience: Experience | undefined) => {
    if (!experience) {
      return;
    }
    const itemList = convertFallbackToViewItemListEvent(experience);
    if (!itemList) {
      return;
    }
    publisher.actions.publish(GoogleAnalyticsEventType.VIEW_ITEM_LIST, {
      continuous_pricing: hasContinuousPricingFromExperience(experience),
      ecommerce: { items: itemList.items },
    });
  };

  return {
    actions: {
      publishPageView,
      publishPartialPageView,
      publishDefaultLayer,
      publishPurchaseEvent,
      publishModifyBidEvent,
      publishCancelEvent,
      publishTogglePointsEvent,
      publishInsufficientPointsBalanceEvent,
      publishViewItemList,
      publishViewIneligibleItemList,
      publishLanguageChange,
      publishCloseCookieBanner,
      publishSliderInteractionEvent,
      publishCartClick,
      publishViewCart,
      publishBeginCheckout,
      publishAddToCart,
      publishRemoveFromCart,
      publishClick,
      publishPageLoadPerformance,
      publishCheckEligibilitySubmit,
      publishCheckEligibilityResponse,
      publishDefaultLayerWithoutExperience,
      publishSeatMapToggle,
      publishSeatMapSeatInteraction,
      publishConfig,
      publishOptimizely,
      publishViewFallbackItemListEvent,
      publishOpenDevPanel,
    },
  };
};

export default useGoogleAnalytics;
